import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { checkAuthStatus, disconnectGoogle, getAuthUrl, login as loginWithPassword, logout } from './api';
import { clearPersistedQueryCache } from './queryCachePersistence';
import Calendar from './components/Calendar';
import Notes, { useNotesState } from './components/Notes';
import Login from './components/Login';
import { Calendar as CalendarIcon, ListTodo, Settings } from 'lucide-react';
import { Button } from './components/catalyst/button';

// Parse the current URL path into app state
function parseRoute() {
  const path = window.location.pathname;
  if (path.startsWith('/notes/')) {
    return { workspace: 'notes', noteId: path.slice(7) }; // /notes/<id>
  }
  if (path === '/notes') {
    return { workspace: 'notes', noteId: null };
  }
  if (path === '/finance') {
    return { workspace: 'finance', noteId: null };
  }
  // /calendar or / or anything else
  return { workspace: 'calendar', noteId: null };
}

function navigate(path, replace = false) {
  if (window.location.pathname === path) return;
  if (replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }
}

function msUntilNextHour() {
  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setHours(now.getHours() + 1, 0, 0, 0);
  return nextHour.getTime() - now.getTime();
}

function App() {
  const queryClient = useQueryClient();
  const hourlyRefreshKeyRef = useRef('');
  const staleRefreshRef = useRef({ timers: {}, lastAttemptAt: {} });
  const [user, setUser] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [financeLoaded, setFinanceLoaded] = useState(() => parseRoute().workspace === 'finance');

  // Derive initial state from URL
  const initial = parseRoute();
  const [notesLoaded, setNotesLoaded] = useState(initial.workspace === 'notes');
  const notesState = useNotesState(!notesLoaded);
  const [workspace, setWorkspace] = useState(initial.workspace);
  const [mobileTab, setMobileTab] = useState('calendar');
  const [initialNoteId] = useState(initial.noteId);

  // Sync URL when mobile tabs change
  const navigateToMobileTab = useCallback((tab) => {
    const nextTab = ['calendar', 'todo', 'settings'].includes(tab) ? tab : 'calendar';
    setMobileTab(nextTab);
    navigate('/calendar');
  }, []);

  // Called by Notes when a note is opened or closed
  const onNoteNavigate = useCallback((noteId) => {
    if (noteId) {
      navigate(`/notes/${noteId}`);
    } else {
      navigate('/notes');
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const status = await checkAuthStatus();
    if (status.authenticated) {
      setUser(status.user);
      setGoogleConnected(!!status.googleConnected);
      setGoogleUser(status.googleUser || null);

      if (!status.googleConnected) {
        queryClient.removeQueries({ queryKey: ['events'] });
        queryClient.removeQueries({ queryKey: ['tasks'] });
        queryClient.removeQueries({ queryKey: ['calendars'] });
        clearPersistedQueryCache().catch(() => {});
      }
    } else {
      queryClient.clear();
      clearPersistedQueryCache().catch(() => {});
      setUser(null);
      setGoogleConnected(false);
      setGoogleUser(null);
    }
    setLoading(false);
  }, [queryClient]);

  // Listen for browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const route = parseRoute();
      setWorkspace(route.workspace);
      if (route.workspace === 'notes') setNotesLoaded(true);
      if (route.workspace === 'finance') setFinanceLoaded(true);
      if (isMobile) {
        setMobileTab('calendar');
      }
      // Dispatch custom event so Notes can react to noteId changes
      window.dispatchEvent(new CustomEvent('route-change', { detail: route }));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMobile]);

  // Redirect bare `/` to `/calendar`
  useEffect(() => {
    if (window.location.pathname === '/') {
      navigate('/calendar', true);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const refreshState = staleRefreshRef.current;

    const handleStaleCache = (event) => {
      const queryKey = event.detail?.queryKey;
      if (!Array.isArray(queryKey) || !queryKey.length) return;

      const rootKey = queryKey[0];
      const now = Date.now();

      if (now - (refreshState.lastAttemptAt[rootKey] || 0) < 60 * 1000) {
        return;
      }

      refreshState.lastAttemptAt[rootKey] = now;
      window.clearTimeout(refreshState.timers[rootKey]);
      refreshState.timers[rootKey] = window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
        delete refreshState.timers[rootKey];
      }, 12 * 1000);
    };

    window.addEventListener('calendar-app-cache-stale', handleStaleCache);

    return () => {
      Object.values(refreshState.timers).forEach(timer => window.clearTimeout(timer));
      refreshState.timers = {};
      window.removeEventListener('calendar-app-cache-stale', handleStaleCache);
    };
  }, [queryClient]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const route = parseRoute();
    if (route.workspace === 'notes' || route.workspace === 'finance') {
      setWorkspace('calendar');
      setMobileTab('calendar');
      navigate('/calendar', true);
    }
  }, [isMobile]);

  const refreshGoogleData = useCallback(() => {
    if (!googleConnected) return;

    const hourKey = new Date().toISOString().slice(0, 13);
    if (hourlyRefreshKeyRef.current === hourKey) return;
    hourlyRefreshKeyRef.current = hourKey;

    queryClient.invalidateQueries({ queryKey: ['events'], refetchType: 'active' });
    queryClient.invalidateQueries({ queryKey: ['tasks'], refetchType: 'active' });
    queryClient.invalidateQueries({ queryKey: ['calendars'], refetchType: 'active' });
  }, [googleConnected, queryClient]);

  useEffect(() => {
    if (!googleConnected) return undefined;

    let timeoutId;

    const scheduleNextRefresh = () => {
      timeoutId = window.setTimeout(() => {
        refreshGoogleData();
        scheduleNextRefresh();
      }, msUntilNextHour());
    };

    scheduleNextRefresh();

    const handleVisibilityChange = () => {
      if (!document.hidden) refreshGoogleData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [googleConnected, refreshGoogleData]);

  const handleLogin = async (username, password) => {
    await loginWithPassword(username, password);
    await checkAuth();
  };

  const handleConnectGoogle = async () => {
    const authUrl = await getAuthUrl();
    window.location.href = authUrl;
  };

  const handleDisconnectGoogle = async () => {
    await disconnectGoogle();
    queryClient.removeQueries({ queryKey: ['events'] });
    queryClient.removeQueries({ queryKey: ['tasks'] });
    queryClient.removeQueries({ queryKey: ['calendars'] });
    await clearPersistedQueryCache();
    setGoogleConnected(false);
    setGoogleUser(null);
  };

  const handleLogout = async () => {
    await logout();
    queryClient.clear();
    await clearPersistedQueryCache();
    setUser(null);
    setGoogleConnected(false);
    setGoogleUser(null);
  };

  if (loading) {
    return <div style={{ height: '100vh', backgroundColor: '#fff' }} />;
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // Mobile layout - calendar, tasks, and settings only.
  if (isMobile) {
    const activeMobileTab = ['calendar', 'todo', 'settings'].includes(mobileTab) ? mobileTab : 'calendar';

    const MobileBottomBar = (
      <div className="flex-shrink-0 border-t border-zinc-200 bg-white/95 px-2 pt-2 shadow-[0_-10px_24px_rgba(24,24,27,0.06)] backdrop-blur" style={{ paddingBottom: 'max(10px, env(safe-area-inset-bottom))' }}>
        <div className="grid grid-cols-3 gap-1">
          {[
            { id: 'calendar', label: 'Calendar', icon: CalendarIcon },
            { id: 'todo', label: 'Tasks', icon: ListTodo },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map(({ id, label, icon: Icon }) => {
            const active = activeMobileTab === id;

            return (
              <Button
                key={id}
                plain
                onClick={() => navigateToMobileTab(id)}
                className={`flex min-h-[56px] flex-col gap-1 rounded-xl px-1 py-2 shadow-none ${
                  active ? '!bg-transparent text-blue-600 hover:!bg-transparent active:!bg-transparent' : 'text-zinc-500 hover:bg-zinc-50'
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? 'text-blue-600' : 'text-zinc-500'}`} />
                <span className={`text-[11px] leading-none ${active ? 'font-bold text-blue-600' : 'font-medium'}`}>{label}</span>
              </Button>
            );
          })}
        </div>
      </div>
    );

    return (
      <div className="fixed inset-0 flex flex-col">
        <div className="flex-1 overflow-hidden relative">
          <div className="flex-col w-full h-full">
            <Calendar
              user={user}
              onLogout={handleLogout}
              googleConnected={googleConnected}
              googleUser={googleUser}
              onConnectGoogle={handleConnectGoogle}
              onDisconnectGoogle={handleDisconnectGoogle}
              mobileTab={activeMobileTab}
              onMobileTabChange={navigateToMobileTab}
              renderBottomBar={MobileBottomBar}
            />
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="fixed inset-0 flex bg-zinc-100 text-zinc-950">
      <main className="relative min-w-0 flex-1 overflow-hidden">
        <div style={{ display: workspace === 'calendar' ? 'block' : 'none' }} className="w-full h-full">
          <Calendar
            user={user}
            onLogout={handleLogout}
            googleConnected={googleConnected}
            googleUser={googleUser}
            onConnectGoogle={handleConnectGoogle}
            onDisconnectGoogle={handleDisconnectGoogle}
          />
        </div>
        <div style={{ display: workspace === 'notes' ? 'block' : 'none' }} className="w-full h-full">
          {notesLoaded && (
            <Notes notesState={notesState} isMobile={false} key="notes" onNoteNavigate={onNoteNavigate} initialNoteId={initialNoteId} />
          )}
        </div>
        {financeLoaded && (
          <iframe
            src="https://personalfinance1-chi.vercel.app"
            title="Finance"
            className="w-full h-full border-0 absolute inset-0"
            style={{ display: workspace === 'finance' ? 'block' : 'none' }}
          />
        )}
      </main>
    </div>
  );
}

export default App;
